import { useState } from "react";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, cx, DurationInput, EmptyState, Field, NumberStepper, Select, TextField, Textarea } from "@/components/ui";
import { Avatar, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { useAction, usePenalties, useRewardTypes, useStore } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { formatUnits, isBanked, planDeduction } from "@shared/economy";
import { PENALTY_REASONS } from "@shared/templates";
import type { DeductionResultDTO, KidSummaryDTO } from "@shared/types";

/** Discipline: deduct from the general balance or a specific reward (e.g. dock screen time). */
export function DeductSheet({ summary, onClose }: { summary: KidSummaryDTO; onClose: () => void }) {
  const { family } = useSession();
  const kid = summary.kid;
  const presets = usePenalties();
  const rewards = useRewardTypes();
  const targets = (rewards.data?.rewardTypes ?? []).filter((t) => t.active && (t.kind === "time" || t.kind === "cash") && t.pointsPerUnit);
  const [targetId, setTargetId] = useState("");
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const target = targets.find((t) => t.id === targetId) ?? null;
  const bank = summary.banks.find((b) => b.rewardTypeId === targetId);
  const plan = planDeduction({
    amount,
    balance: summary.balance,
    floor: summary.floor,
    rewardType: target,
    bankBalance: bank?.balance ?? 0,
  });
  const deduct = useAction(
    () => api.post<DeductionResultDTO>(`/kids/${kid.id}/deduct`, { amount, rewardTypeId: targetId || null, reason: reason.trim(), note: note.trim() || undefined }),
    {
      success: (r) =>
        r.points === 0 && r.bankUnits === 0
          ? `Nothing deducted — ${kid.name} is at the floor`
          : r.clampedByFloor
            ? `Deducted what was possible — ${kid.name} hit the floor`
            : "Deduction applied",
      onSuccess: onClose,
    },
  );

  return (
    <Sheet
      open
      onClose={onClose}
      title="Apply a consequence"
      footer={
        <Button block size="lg" variant="danger" onClick={() => deduct.mutate()} loading={deduct.isPending} disabled={!reason.trim() || amount <= 0}>
          Deduct
        </Button>
      }
    >
      <div className="mb-4 flex items-center gap-3 rounded-2xl bg-sunken p-3">
        <Avatar member={kid} />
        <div className="text-sm">
          <div className="font-extrabold">{kid.name}</div>
          <div className="text-muted">
            Balance <Points value={summary.balance} family={family} /> · floor {summary.floor === 0 ? "0" : summary.floor}
          </div>
        </div>
      </div>

      {(presets.data?.presets.length ?? 0) > 0 && (
        <Field label="Common reasons">
          <div className="flex flex-wrap gap-2">
            {presets.data!.presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setReason(p.name);
                  setAmount(p.amount);
                  setTargetId(p.rewardTypeId ?? "");
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-sunken px-3 py-1.5 text-sm font-bold ring-1 ring-line hover:ring-danger"
              >
                {p.icon} {p.name}
              </button>
            ))}
          </div>
        </Field>
      )}

      <div className="mt-5 space-y-5">
        <Field label="Take from">
          <Select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">{family.currencyIcon} General balance</option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.icon} {t.name}
                {t.kind === "time" ? " (saved time first)" : ""}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={target ? (target.kind === "time" ? "Amount" : `Amount (${target.kind === "cash" ? "$" : target.unitLabel})`) : `Amount (${family.currencyName})`}>
          {target?.kind === "time" ? (
            <DurationInput value={amount} onChange={setAmount} min={1} max={24 * 60} step={5} />
          ) : (
            <NumberStepper value={amount} onChange={setAmount} min={1} max={100000} step={target?.kind === "cash" ? 1 : 5} />
          )}
        </Field>
        <TextField
          label="Reason (your kid sees this)"
          list="penalty-reasons"
          value={reason}
          maxLength={120}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Not following instructions"
        />
        <datalist id="penalty-reasons">
          {PENALTY_REASONS.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
        <Field label="Note (optional)">
          <Textarea value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="What happened, and how to make it right" />
        </Field>
      </div>

      <div className="mt-5 rounded-2xl bg-danger-soft p-3 text-sm font-semibold text-danger">
        {plan.bankUnits === 0 && plan.points === 0 ? (
          <>Nothing to deduct — {kid.name} is already at the floor.</>
        ) : (
          <>
            Will remove{" "}
            {plan.bankUnits > 0 && target && isBanked(target.kind) && <b>{formatUnits(plan.bankUnits, target)} of saved {target.name}</b>}
            {plan.bankUnits > 0 && plan.points > 0 && " and "}
            {plan.points > 0 && (
              <b>
                {plan.points} {family.currencyName}
              </b>
            )}
            .
            {plan.clampedByFloor && <> Limited by the floor ({summary.floor}).</>}
          </>
        )}
      </div>
    </Sheet>
  );
}

const BONUS_REASONS = ["Great attitude", "Helped without being asked", "Kindness to a sibling", "Extra effort", "Brave try"];

export function BonusSheet({ summary, onClose }: { summary: KidSummaryDTO; onClose: () => void }) {
  const { family } = useSession();
  const [amount, setAmount] = useState(10);
  const [reason, setReason] = useState("");
  const bonus = useAction(() => api.post(`/kids/${summary.kid.id}/bonus`, { amount, reason: reason.trim() }), {
    success: `Bonus sent to ${summary.kid.name} 🎉`,
    onSuccess: onClose,
  });
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Bonus for ${summary.kid.name}`}
      footer={
        <Button block size="lg" variant="coin" onClick={() => bonus.mutate()} loading={bonus.isPending} disabled={!reason.trim() || amount < 1}>
          Give <Points value={amount} family={family} />
        </Button>
      }
    >
      <div className="space-y-5">
        <Field label={`Amount (${family.currencyName})`}>
          <NumberStepper value={amount} onChange={(v) => setAmount(Math.round(v))} min={1} step={5} />
        </Field>
        <Field label="For">
          <div className="mb-2 flex flex-wrap gap-2">
            {BONUS_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setReason(r)} className="rounded-full bg-sunken px-3 py-1.5 text-sm font-bold ring-1 ring-line hover:ring-coin">
                {r}
              </button>
            ))}
          </div>
          <TextField label="" aria-label="Reason" value={reason} maxLength={120} onChange={(e) => setReason(e.target.value)} placeholder="Why they earned it" />
        </Field>
      </div>
    </Sheet>
  );
}

export function RecoverySheet({ summary, onClose }: { summary: KidSummaryDTO; onClose: () => void }) {
  const { family } = useSession();
  const owed = Math.max(0, -summary.balance);
  const [amount, setAmount] = useState(Math.min(10, owed) || 1);
  const [note, setNote] = useState("");
  const credit = useAction(() => api.post(`/kids/${summary.kid.id}/recovery`, { amount, note: note.trim() || undefined }), {
    success: "Credit applied — nice work, kiddo",
    onSuccess: onClose,
  });
  return (
    <Sheet
      open
      onClose={onClose}
      title="Work it off"
      footer={
        <Button block size="lg" onClick={() => credit.mutate()} loading={credit.isPending} disabled={amount < 1}>
          Credit <Points value={Math.min(amount, owed)} family={family} />
        </Button>
      }
    >
      <p className="text-sm text-muted">
        Reward good behavior with a credit that only goes toward getting back to zero. {summary.kid.name} is at{" "}
        <b className="text-danger">{summary.balance}</b>.
      </p>
      <div className="mt-4 space-y-5">
        <NumberStepper value={amount} onChange={(v) => setAmount(Math.round(v))} min={1} max={Math.max(1, owed)} step={5} />
        <TextField label="For (optional)" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="Apologized and made it right" />
      </div>
      <Badge tone="violet" className="mt-4">
        Credits never push a balance above zero
      </Badge>
    </Sheet>
  );
}

/** Redeem a family-store reward for a kid (e.g. handing over a treat in person). It's approved right away. */
export function RedeemSheet({ summary, onClose }: { summary: KidSummaryDTO; onClose: () => void }) {
  const { family } = useSession();
  const kid = summary.kid;
  const store = useStore();
  const [itemId, setItemId] = useState<string | null>(null);
  const items = (store.data?.items ?? []).filter((i) => i.active && (i.kidIds.length === 0 || i.kidIds.includes(kid.id)));
  const item = items.find((i) => i.id === itemId) ?? null;
  const redeem = useAction(() => api.post(`/store/${itemId}/redeem`, { kidId: kid.id }), {
    success: `${item?.name ?? "Reward"} redeemed for ${kid.name} — mark it handed over when they have it`,
    onSuccess: onClose,
  });
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Redeem for ${kid.name}`}
      footer={
        <Button block size="lg" onClick={() => redeem.mutate()} loading={redeem.isPending} disabled={!item}>
          {item ? (
            <>
              Redeem · <Points value={item.price} family={family} />
            </>
          ) : (
            "Pick a reward"
          )}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-muted">
        {kid.name} has <Points value={summary.available} family={family} /> to spend. Redeeming here approves it right away.
      </p>
      {items.length === 0 ? (
        <EmptyState icon="🛍️" title="No rewards in the store yet">
          Add some under Plan → Family store.
        </EmptyState>
      ) : (
        <div className="space-y-2" role="radiogroup" aria-label="Reward">
          {items.map((i) => {
            const soldOut = i.stock !== null && i.stock <= 0;
            const short = i.price > summary.available;
            return (
              <button
                key={i.id}
                type="button"
                role="radio"
                aria-checked={i.id === itemId}
                disabled={soldOut || short}
                onClick={() => setItemId(i.id)}
                className={cx(
                  "flex min-h-14 w-full items-center gap-3 rounded-2xl px-3 py-2 text-left ring-1 transition disabled:opacity-50",
                  i.id === itemId ? "bg-brand-soft ring-2 ring-brand" : "bg-sunken ring-line",
                )}
              >
                <span className="text-2xl">{i.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold">{i.name}</span>
                  {(soldOut || short) && <span className="block text-xs font-bold text-muted">{soldOut ? "Sold out" : "Not enough yet"}</span>}
                </span>
                <Points value={i.price} family={family} className="font-extrabold text-coin-ink" />
              </button>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
